import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Chip,
	Grid,
	IconButton,
	Link,
	Pagination,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tooltip,
	Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import PassengerBillingSettings from '../PassengerBillingSettings';
import { PassengerCreditTerm } from 'features/creditRules/components/PassengerCreditTerm';
import PassengerAccountStatement from '../PassengerAccountStatement';
import SaleOrderInvoicingStatusCell from 'features/salesOrder/components/SaleOrderInvoicingStatusCell';
import { ISaleOrderListItem } from 'features/salesOrder/types';

import routes from '../../../../constants/routes';
import { todayIso } from 'features/invoices/uiHelpers';
import { getLabelNameForMunicipality, getLabelNameForProvince } from '../../helpers';
import {
	buildBillingDocumentLabel,
	DetailField,
	filterDetailFields,
	formatDetailAmount,
	formatDetailDate,
	formatDetailDisplayName,
	formatDetailSubtitle,
	filterSaleOrdersByBalance,
	getDetailSectionVisibility,
	mapSaleOrderBalanceFilterToHasPending,
	resolvePassengerCommercialSummary,
	resolvePendingBalanceChipColor,
	sortSaleOrdersByDepartureDate,
	SaleOrderBalanceFilter,
	SaleOrderDepartureSort,
} from '../../helpers/detailView';
import { buildSalesOrderListUrl } from 'features/salesOrder/helpers/salesOrderListState';
import {
	IPassengerDetail,
	IPassengerDocument,
	IPassengerFrequentFlyerNumberToPreview,
	IPassengerRelatedWithSaleOrderDetailToPreview,
	PassengerDetailViewMode,
} from '../../types';

const SALE_ORDERS_PAGE_SIZE = 10;
const CRM_DEAL_URL = 'https://crm.mevuelo.com/dashboard/cases/';

/**
 * ¿El viaje ya terminó? Undefined cuando no hay con qué saberlo — que no es lo
 * mismo que "no terminó", y el semáforo trata los dos casos distinto.
 * Sirve tanto si la API manda el booleano como si manda la fecha de vuelta.
 */
const resolveReturnDateReached = (order: IPassengerRelatedWithSaleOrderDetailToPreview): boolean | undefined => {
	if (order.isReturnDateReached !== undefined) return order.isReturnDateReached;
	if (!order.travelReturnDate) return undefined;
	return order.travelReturnDate.slice(0, 10) <= todayIso();
};

interface PassengerDetailProps {
	passengerDetail: IPassengerDetail;
	viewMode: PassengerDetailViewMode;
}

const sectionPaperSx = {
	p: 2.5,
	mb: 2,
	borderRadius: 2,
	border: '1px solid rgba(0,0,0,0.08)',
	boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
};

const formatTravelDocumentType = (documentType: string): string => {
	switch (documentType) {
		case 'Passport':
			return 'Pasaporte';
		case 'DriverLicense':
			return 'Libreta de conducir';
		case 'Cedula':
			return 'Cédula';
		default:
			return documentType;
	}
};

const DetailSection = ({
	title,
	children,
	headerAction,
}: {
	title: string;
	children: React.ReactNode;
	headerAction?: React.ReactNode;
}): JSX.Element => (
	<Paper elevation={0} sx={sectionPaperSx}>
		<Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" mb={2}>
			<Typography variant="h6" sx={{ fontWeight: 600 }}>
				{title}
			</Typography>
			{headerAction}
		</Box>
		{children}
	</Paper>
);

const DetailFieldGrid = ({ fields }: { fields: DetailField[] }): JSX.Element => {
	const visibleFields = filterDetailFields(fields);

	if (!visibleFields.length) {
		return (
			<Typography variant="body2" color="text.secondary">
				Sin información para mostrar.
			</Typography>
		);
	}

	return (
		<Grid container spacing={2}>
			{visibleFields.map((field) => (
				<Grid item xs={12} sm={6} md={4} key={field.label}>
					<Typography variant="caption" color="text.secondary" display="block">
						{field.label}
					</Typography>
					<Typography variant="body1">{field.value}</Typography>
				</Grid>
			))}
		</Grid>
	);
};

const PassengerDetail = ({ passengerDetail, viewMode }: PassengerDetailProps): JSX.Element => {
	const {
		keyId,
		isPhysicalPerson,
		name,
		lastName,
		birthDate,
		countryName,
		isSameCountryOfMarket,
		countryCodeOfMarket,
		state,
		province,
		municipality,
		address,
		postalCode,
		phone,
		email,
		taxPayerTypeName,
		taxRegimeName,
		taxResidenceTypeName,
		fiscalResponsibilityName,
		documentTypeName,
		documentNumber,
		documentVerificationNumber,
		seatPreference,
		documents,
		frequentFlyerNumbers,
		saleOrdersRelated,
	} = passengerDetail;

	const [saleOrdersPage, setSaleOrdersPage] = useState(1);
	const [saleOrdersBalanceFilter, setSaleOrdersBalanceFilter] = useState<SaleOrderBalanceFilter>('all');
	const [saleOrdersDepartureSort, setSaleOrdersDepartureSort] = useState<SaleOrderDepartureSort>('desc');
	const [accountStatementOpen, setAccountStatementOpen] = useState(false);
	const isClientView = viewMode === 'client';

	/**
	 * SaleOrderInvoicingStatusCell lee estos campos del row del listado de
	 * órdenes; se sintetizan desde la orden relacionada del detail.
	 *
	 * `GET /passengers/detail/{id}` todavía no devuelve `segmentType` ni la
	 * fecha de vuelta, que son los dos datos con los que se decide si urge
	 * facturar. Se leen igual: mientras no vengan quedan undefined y la celda
	 * detecta que no puede resolver la urgencia, y el día que la API los
	 * agregue el semáforo empieza a marcar solo. Antes acá iba
	 * `isReturnDateReached: false`, y eso hacía que toda orden pendiente dijera
	 * "se factura al finalizar el viaje" — incluso las corpo, que se facturan lo
	 * antes posible sin importar el país.
	 */
	const invoicingCellRow = (order: IPassengerRelatedWithSaleOrderDetailToPreview): ISaleOrderListItem =>
		({
			invoicedAmount: order.invoicedAmount ?? 0,
			saleAmount: order.saleAmount ?? undefined,
			pendingInvoicingAmount:
				order.saleAmount != null ? Math.max(0, order.saleAmount - (order.invoicedAmount ?? 0)) : undefined,
			invoicingStatus: order.invoicingStatus ?? undefined,
			segmentType: order.segmentType,
			isReturnDateReached: resolveReturnDateReached(order),
		}) as unknown as ISaleOrderListItem;

	const sections = getDetailSectionVisibility(passengerDetail, viewMode);
	const billingDocumentLabel = buildBillingDocumentLabel(passengerDetail);
	const commercialSummary = useMemo(
		() => resolvePassengerCommercialSummary(passengerDetail, sections.filterInvoiceableSaleOrdersOnly),
		[passengerDetail, sections.filterInvoiceableSaleOrdersOnly],
	);
	const pendingBalanceChipColor = resolvePendingBalanceChipColor(commercialSummary);

	const invoiceableSaleOrders = useMemo(() => {
		const orders = saleOrdersRelated ?? [];
		if (!sections.filterInvoiceableSaleOrdersOnly) return orders;
		return orders.filter((order) => order.invoiceable);
	}, [saleOrdersRelated, sections.filterInvoiceableSaleOrdersOnly]);

	const displayedSaleOrders = useMemo(() => {
		const byBalance = filterSaleOrdersByBalance(invoiceableSaleOrders, saleOrdersBalanceFilter);
		return sortSaleOrdersByDepartureDate(byBalance, saleOrdersDepartureSort);
	}, [invoiceableSaleOrders, saleOrdersBalanceFilter, saleOrdersDepartureSort]);

	const saleOrdersPageTotal = Math.max(1, Math.ceil(displayedSaleOrders.length / SALE_ORDERS_PAGE_SIZE));
	const paginatedSaleOrders = useMemo(() => {
		const start = (saleOrdersPage - 1) * SALE_ORDERS_PAGE_SIZE;
		return displayedSaleOrders.slice(start, start + SALE_ORDERS_PAGE_SIZE);
	}, [displayedSaleOrders, saleOrdersPage]);

	useEffect(() => {
		setSaleOrdersPage(1);
	}, [
		invoiceableSaleOrders.length,
		sections.filterInvoiceableSaleOrdersOnly,
		saleOrdersBalanceFilter,
		saleOrdersDepartureSort,
	]);

	const handleDepartureSortToggle = () => {
		setSaleOrdersDepartureSort((prev) => (prev === 'desc' ? 'asc' : 'desc'));
	};

	const identityFields: DetailField[] = [
		{ label: 'ID', value: keyId },
		{ label: isPhysicalPerson ? 'Nombres' : 'Razón social', value: name },
		{ label: isPhysicalPerson ? 'Apellidos' : 'Nombre fantasía', value: lastName },
		...(sections.showBirthDate ? [{ label: 'Fecha de nacimiento', value: formatDetailDate(birthDate) }] : []),
		{ label: 'País', value: countryName },
	];

	const contactLocationFields: DetailField[] = [
		{ label: 'Teléfono', value: phone },
		{ label: 'Email', value: email },
		{ label: 'Dirección', value: address },
		...(isSameCountryOfMarket
			? [
					{ label: 'Estado / Departamento', value: state },
					{ label: getLabelNameForProvince(countryCodeOfMarket), value: province },
					{ label: getLabelNameForMunicipality(countryCodeOfMarket), value: municipality },
			  ]
			: []),
		{ label: 'Código postal', value: postalCode },
	];

	const billingFields: DetailField[] = [
		{ label: 'Tipo', value: documentTypeName },
		{ label: 'Documento de facturación', value: documentNumber },
		{ label: 'Número de verificación', value: documentVerificationNumber },
	];

	const fiscalFields: DetailField[] = [
		{ label: 'Tipo de contribuyente', value: taxPayerTypeName },
		{ label: 'Régimen fiscal', value: taxRegimeName },
		{ label: 'Tipo de residencia fiscal', value: taxResidenceTypeName },
		{ label: 'Responsabilidad fiscal', value: fiscalResponsibilityName },
	];

	const passengerExtraFields: DetailField[] = [{ label: 'Preferencia de asiento', value: seatPreference }];

	const handleOpenSaleOrder = (saleOrderId: string) => {
		const newUrl = routes.salesOrder.edit.replace(':id', saleOrderId);
		window.open(`${newUrl}?passengerId=${keyId}`, '_blank', 'noopener,noreferrer');
	};

	const saleOrdersTitle = sections.filterInvoiceableSaleOrdersOnly
		? 'Órdenes de venta facturables'
		: 'Órdenes de venta relacionadas';

	const handleOpenSalesOrderList = () => {
		const url = buildSalesOrderListUrl({
			customerCompleteName: formatDetailDisplayName(passengerDetail),
			hasPendingPayAmount: mapSaleOrderBalanceFilterToHasPending(saleOrdersBalanceFilter),
			departureSort: saleOrdersDepartureSort,
		});
		window.open(`${window.location.origin}${url}`, '_blank', 'noopener,noreferrer');
	};

	return (
		<Box>
			<Paper
				elevation={0}
				sx={{
					...sectionPaperSx,
					background: 'linear-gradient(135deg, rgba(63, 81, 181, 0.08) 0%, rgba(255,255,255,1) 100%)',
				}}
			>
				<Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 600 }}>
							{formatDetailDisplayName(passengerDetail)}
						</Typography>
						{formatDetailSubtitle(passengerDetail) && (
							<Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
								{formatDetailSubtitle(passengerDetail)}
							</Typography>
						)}
						<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
							{countryName}
							{billingDocumentLabel ? ` · ${billingDocumentLabel}` : ''}
						</Typography>
					</Box>
					<Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
						{isClientView && (
							<Button
								variant="contained"
								size="small"
								startIcon={<ReceiptLongIcon />}
								onClick={() => setAccountStatementOpen(true)}
							>
								Estado de cuenta
							</Button>
						)}
						<Chip
							icon={isPhysicalPerson ? <PersonIcon /> : <BusinessIcon />}
							label={isPhysicalPerson ? 'Persona física' : 'Persona jurídica'}
							color={isPhysicalPerson ? 'success' : 'info'}
							variant="outlined"
						/>
						<Chip label={viewMode === 'client' ? 'Cliente' : 'Pasajero'} color="primary" variant="outlined" />
						{sections.showCommercialSummary && commercialSummary.pendingPayAmount !== undefined && (
							<Chip
								icon={<AccountBalanceWalletIcon />}
								label={`Saldo pendiente USD ${formatDetailAmount(commercialSummary.pendingPayAmount)}`}
								color={pendingBalanceChipColor}
								variant={pendingBalanceChipColor === 'default' ? 'outlined' : 'filled'}
							/>
						)}
						{sections.showCommercialSummary && commercialSummary.hasFutureTrips !== undefined && (
							<Chip
								icon={<FlightTakeoffIcon />}
								label={commercialSummary.hasFutureTrips ? 'Viajes futuros' : 'Sin viajes futuros'}
								color={commercialSummary.hasFutureTrips ? 'info' : 'default'}
								variant={commercialSummary.hasFutureTrips ? 'filled' : 'outlined'}
							/>
						)}
					</Box>
				</Box>
			</Paper>

			{sections.showCommercialSummary && (
				<DetailSection title="Resumen comercial">
					<Grid container spacing={2}>
						<Grid item xs={12} sm={4}>
							<Typography variant="caption" color="text.secondary" display="block">
								Saldo pendiente
							</Typography>
							<Typography
								variant="h6"
								sx={
									(commercialSummary.pendingPayAmount ?? 0) > 0 ? { color: 'error.main', fontWeight: 700 } : undefined
								}
							>
								{formatDetailAmount(commercialSummary.pendingPayAmount)}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography variant="caption" color="text.secondary" display="block">
								Tiene saldo pendiente
							</Typography>
							<Typography variant="body1">
								{commercialSummary.hasPendingPayAmount === undefined
									? '—'
									: commercialSummary.hasPendingPayAmount
									? 'Sí'
									: 'No'}
							</Typography>
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography variant="caption" color="text.secondary" display="block">
								Viajes futuros
							</Typography>
							<Typography variant="body1">
								{commercialSummary.hasFutureTrips === undefined ? '—' : commercialSummary.hasFutureTrips ? 'Sí' : 'No'}
							</Typography>
						</Grid>
					</Grid>

					{/* Límite de crédito: la exposición es el saldo general de cobro (facturado o no) */}
					{passengerDetail.maximumCreditAmount != null &&
						(commercialSummary.pendingPayAmount ?? 0) > passengerDetail.maximumCreditAmount && (
							<Alert severity="error" sx={{ mt: 2 }}>
								⚠️ Supera su límite de crédito: saldo USD {formatDetailAmount(commercialSummary.pendingPayAmount)} /
								tope USD {formatDetailAmount(passengerDetail.maximumCreditAmount)} (excedente USD{' '}
								{formatDetailAmount((commercialSummary.pendingPayAmount ?? 0) - passengerDetail.maximumCreditAmount)}
								).
							</Alert>
						)}
				</DetailSection>
			)}

			{sections.showIdentityFields && (
				<DetailSection title="Identidad">
					<DetailFieldGrid fields={identityFields} />
				</DetailSection>
			)}

			<DetailSection title="Contacto y ubicación">
				<DetailFieldGrid fields={contactLocationFields} />
			</DetailSection>

			{sections.showBillingDocument && filterDetailFields(billingFields).length > 0 && (
				<DetailSection title="Documento de facturación">
					<DetailFieldGrid fields={billingFields} />
				</DetailSection>
			)}

			{isClientView && !isPhysicalPerson && (
				<DetailSection title="Configuración de facturación">
					<PassengerBillingSettings passengerDetail={passengerDetail} />
				</DetailSection>
			)}

			{/* Condición de crédito: en vista cliente, también para personas
			    físicas — hay clientes corpo que facturan como persona física. */}
			{isClientView && (
				<DetailSection title="Condición de crédito">
					<PassengerCreditTerm
						passengerId={passengerDetail.keyId}
						passengerName={passengerDetail.name}
						passengerDocument={passengerDetail.documentNumber}
					/>
				</DetailSection>
			)}

			{sections.showFiscalData && filterDetailFields(fiscalFields).length > 0 && (
				<DetailSection title="Datos fiscales">
					<DetailFieldGrid fields={fiscalFields} />
				</DetailSection>
			)}

			{sections.showSeatPreference && filterDetailFields(passengerExtraFields).length > 0 && (
				<DetailSection title="Preferencias de viaje">
					<DetailFieldGrid fields={passengerExtraFields} />
				</DetailSection>
			)}

			{sections.showTravelDocuments && (
				<DetailSection title="Documentos de viaje">
					{documents?.length ? (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Tipo</TableCell>
										<TableCell>País emisor</TableCell>
										<TableCell>Número</TableCell>
										<TableCell>Vencimiento</TableCell>
										<TableCell>Imagen</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{documents.map((doc: IPassengerDocument) => (
										<TableRow key={doc.keyId}>
											<TableCell>{formatTravelDocumentType(doc.documentType)}</TableCell>
											<TableCell>{doc.country?.name ?? '—'}</TableCell>
											<TableCell>{doc.documentNumber}</TableCell>
											<TableCell>{formatDetailDate(doc.expirationDate)}</TableCell>
											<TableCell>
												{doc.image ? (
													<a href={doc.image} target="_blank" rel="noopener noreferrer">
														<img
															src={doc.image}
															alt="Documento de viaje"
															style={{ maxWidth: 80, maxHeight: 80, objectFit: 'cover' }}
														/>
													</a>
												) : (
													'—'
												)}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Typography variant="body2" color="text.secondary">
							No hay documentos de viaje registrados.
						</Typography>
					)}
				</DetailSection>
			)}

			{sections.showFrequentFlyer && (
				<DetailSection title="Viajero frecuente">
					{frequentFlyerNumbers?.length ? (
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Aerolínea</TableCell>
										<TableCell>Número</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{frequentFlyerNumbers.map((ff: IPassengerFrequentFlyerNumberToPreview, index) => (
										<TableRow key={`${ff.airlineName}-${index}`}>
											<TableCell>{ff.airlineName}</TableCell>
											<TableCell>{ff.frequentFlyerNumber}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					) : (
						<Typography variant="body2" color="text.secondary">
							No hay programas de viajero frecuente registrados.
						</Typography>
					)}
				</DetailSection>
			)}

			<DetailSection
				title={saleOrdersTitle}
				headerAction={
					sections.filterInvoiceableSaleOrdersOnly ? (
						<Button size="small" variant="outlined" endIcon={<OpenInNewIcon />} onClick={handleOpenSalesOrderList}>
							Ver en listado de órdenes
						</Button>
					) : undefined
				}
			>
				{invoiceableSaleOrders.length ? (
					<>
						<Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5} mb={1.5}>
							<Typography variant="body2" color="text.secondary">
								{displayedSaleOrders.length} de {invoiceableSaleOrders.length} orden
								{invoiceableSaleOrders.length === 1 ? '' : 'es'}
								{saleOrdersBalanceFilter !== 'all' ? ' (filtradas)' : ''}
							</Typography>
							<Box display="flex" gap={1} flexWrap="wrap">
								<Chip
									label="Todas"
									size="small"
									variant={saleOrdersBalanceFilter === 'all' ? 'filled' : 'outlined'}
									color={saleOrdersBalanceFilter === 'all' ? 'primary' : 'default'}
									onClick={() => setSaleOrdersBalanceFilter('all')}
									sx={{ cursor: 'pointer' }}
								/>
								<Chip
									label="Solo con saldo"
									size="small"
									variant={saleOrdersBalanceFilter === 'withBalance' ? 'filled' : 'outlined'}
									color={saleOrdersBalanceFilter === 'withBalance' ? 'warning' : 'default'}
									onClick={() => setSaleOrdersBalanceFilter('withBalance')}
									sx={{ cursor: 'pointer' }}
								/>
								<Chip
									label="Sin saldo"
									size="small"
									variant={saleOrdersBalanceFilter === 'withoutBalance' ? 'filled' : 'outlined'}
									color={saleOrdersBalanceFilter === 'withoutBalance' ? 'success' : 'default'}
									onClick={() => setSaleOrdersBalanceFilter('withoutBalance')}
									sx={{ cursor: 'pointer' }}
								/>
							</Box>
						</Box>
						{displayedSaleOrders.length ? (
							<TableContainer sx={{ overflowX: 'auto' }}>
								<Table size="small" sx={{ minWidth: isClientView ? 1020 : 720 }}>
									<TableHead>
										<TableRow>
											<TableCell>ID</TableCell>
											<TableCell>Deal / Caso</TableCell>
											<TableCell sortDirection={saleOrdersDepartureSort}>
												<TableSortLabel active direction={saleOrdersDepartureSort} onClick={handleDepartureSortToggle}>
													Salida
												</TableSortLabel>
											</TableCell>
											<TableCell align="right">Pendiente (USD)</TableCell>
											{isClientView && (
												<>
													<TableCell align="right">Venta (USD)</TableCell>
													<TableCell align="right">Facturado (USD)</TableCell>
													<TableCell>Facturación</TableCell>
												</>
											)}
											<TableCell>Facturable</TableCell>
											<TableCell align="right">Acciones</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{paginatedSaleOrders.map((order: IPassengerRelatedWithSaleOrderDetailToPreview) => (
											<TableRow key={order.saleOrderId} hover>
												<TableCell>
													<Link
														component="button"
														variant="body2"
														underline="hover"
														onClick={() => handleOpenSaleOrder(order.saleOrderId)}
														sx={{ fontFamily: 'monospace', textAlign: 'left' }}
													>
														{order.saleOrderId}
													</Link>
												</TableCell>
												<TableCell>
													{order.dealId === undefined || order.dealId === null ? (
														'—'
													) : order.isBitrixDeal ? (
														<Tooltip title="Deal de Bitrix">
															<Typography variant="body2" color="primary">
																#{order.dealId}
															</Typography>
														</Tooltip>
													) : (
														<Box display="flex" alignItems="center" gap={0.25}>
															<Tooltip title="Abrir caso en CRM">
																<Typography
																	variant="body2"
																	color="primary"
																	sx={{ cursor: 'pointer' }}
																	onClick={() =>
																		window.open(`${CRM_DEAL_URL}${order.dealId}`, '_blank', 'noopener,noreferrer')
																	}
																>
																	#{order.dealId}
																</Typography>
															</Tooltip>
															<Tooltip title="Abrir caso en CRM">
																<IconButton
																	size="small"
																	sx={{ p: 0.25 }}
																	onClick={() =>
																		window.open(`${CRM_DEAL_URL}${order.dealId}`, '_blank', 'noopener,noreferrer')
																	}
																>
																	<OpenInNewIcon sx={{ fontSize: 14 }} />
																</IconButton>
															</Tooltip>
														</Box>
													)}
												</TableCell>
												<TableCell>{formatDetailDate(order.travelDepartureDate)}</TableCell>
												<TableCell align="right">{formatDetailAmount(order.pendingPayAmount)}</TableCell>
												{isClientView && (
													<>
														<TableCell align="right">{formatDetailAmount(order.saleAmount ?? undefined)}</TableCell>
														<TableCell align="right">{formatDetailAmount(order.invoicedAmount)}</TableCell>
														<TableCell>
															{order.invoicingStatus || order.invoicedAmount !== undefined ? (
																<SaleOrderInvoicingStatusCell row={invoicingCellRow(order)} />
															) : (
																'—'
															)}
														</TableCell>
													</>
												)}
												<TableCell>
													<Chip
														label={order.invoiceable ? 'Sí' : 'No'}
														size="small"
														color={order.invoiceable ? 'success' : 'default'}
														variant="outlined"
													/>
												</TableCell>
												<TableCell align="right">
													<IconButton
														size="small"
														onClick={() => handleOpenSaleOrder(order.saleOrderId)}
														title="Abrir orden de venta"
													>
														<OpenInNewIcon fontSize="small" />
													</IconButton>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<Typography variant="body2" color="text.secondary">
								No hay órdenes que coincidan con el filtro de saldo seleccionado.
							</Typography>
						)}
						{displayedSaleOrders.length > SALE_ORDERS_PAGE_SIZE && (
							<Box display="flex" justifyContent="center" mt={2}>
								<Pagination
									count={saleOrdersPageTotal}
									page={saleOrdersPage}
									onChange={(_, page) => setSaleOrdersPage(page)}
									color="primary"
									size="small"
								/>
							</Box>
						)}
					</>
				) : (
					<Typography variant="body2" color="text.secondary">
						{sections.filterInvoiceableSaleOrdersOnly
							? 'No hay órdenes de venta facturables relacionadas.'
							: 'No hay órdenes de venta relacionadas.'}
					</Typography>
				)}
			</DetailSection>

			{isClientView && (
				<PassengerAccountStatement
					open={accountStatementOpen}
					onClose={() => setAccountStatementOpen(false)}
					passengerId={keyId}
					passengerName={formatDetailDisplayName(passengerDetail)}
				/>
			)}
		</Box>
	);
};

export default PassengerDetail;
